/**
 * Affiliates API
 *
 * Endpoints for affiliate management, partnerships,
 * marketing materials, commissions, and payouts.
 */

import { apiClient } from './client'
import type {
  ApiResponse,
  AdminBankAccountRow,
  BankAccount,
  BankAccountStatus,
  BankAccountType,
  Payout,
  PayoutBalanceSummary,
  PayoutStatus,
  RevealedBankDetails,
} from './types'
import type { CommunicationPreferences, CommunicationPreferencesPatch } from './communication-preferences.types'

// ==================== Affiliate Management ====================

/**
 * Affiliate management endpoints
 */
export const affiliatesApi = {

  /**
   * Update affiliate information
   */
  updateAffiliate: (affiliateId: string, data: Partial<any>): Promise<ApiResponse<any>> =>
    apiClient.put(`/affiliates/${affiliateId}`, data),

  /** #1331 Part 1b-B — admin-only WhatsApp consent + preferred channel. */
  getCommunicationPreferences: (affiliateId: string): Promise<ApiResponse<CommunicationPreferences>> =>
    apiClient.get(`/affiliates/${affiliateId}/communication-preferences`),
  updateCommunicationPreferences: (affiliateId: string, patch: CommunicationPreferencesPatch): Promise<ApiResponse<CommunicationPreferences>> =>
    apiClient.put(`/affiliates/${affiliateId}/communication-preferences`, patch),

  /**
   * Search affiliates with filters
   */
  searchAffiliates: (params?: {
    page?: number
    limit?: number
    search?: string
    status?: string
    tier?: string
  }): Promise<ApiResponse<{ items: any[]; total: number; page: number; totalPages: number }>> =>
    apiClient.get('/affiliates/search', { params }),

  /**
   * #591: Fetch ALL affiliates by walking every page of /affiliates/search.
   * The backend caps `limit` at 100 and orders by createdAt DESC, so a
   * single page-1 call silently drops the oldest affiliates once the
   * platform exceeds 100. Use this for any dropdown/picker that needs the
   * full list. Note: this endpoint nests results as data.{items,totalPages}.
   */
  getAllAffiliates: async (): Promise<any[]> => {
    const firstPage = await affiliatesApi.searchAffiliates({ limit: 100, page: 1 })
    const firstData = firstPage.data as any
    const all = [...(firstData?.items || [])]
    const totalPages = firstData?.totalPages || 1
    if (totalPages > 1) {
      const remaining = []
      for (let page = 2; page <= totalPages; page++) {
        remaining.push(affiliatesApi.searchAffiliates({ limit: 100, page }))
      }
      const results = await Promise.all(remaining)
      results.forEach(r => {
        const items = (r.data as any)?.items
        if (items) all.push(...items)
      })
    }
    return all
  },

  /**
   * Get affiliate dashboard statistics
   */
  getDashboardStats: (affiliateId: string): Promise<ApiResponse<any>> =>
    apiClient.get(`/affiliates/${affiliateId}/dashboard`),

  // ==================== Admin Operations ====================

  /**
   * Approve an affiliate application
   */
  approveAffiliate: (affiliateId: string, data?: {
    commissionRate?: number
    tier?: string
  }): Promise<ApiResponse<void>> =>
    apiClient.post(`/affiliates/${affiliateId}/approve`, data),

  /**
   * Reject an affiliate application
   */
  rejectAffiliate: (affiliateId: string, data: {
    reason: string
  }): Promise<ApiResponse<void>> =>
    apiClient.post(`/affiliates/${affiliateId}/reject`, data),

  /**
   * Suspend an affiliate
   */
  suspendAffiliate: (affiliateId: string, data: {
    reason: string
    expiresAt?: string
  }): Promise<ApiResponse<void>> =>
    apiClient.post(`/affiliates/${affiliateId}/suspend`, data),

  /**
   * Reactivate a suspended affiliate — or, #1324, one that has left the
   * platform (status-only restore to active; hotel relationships and
   * commission rules are NOT restored).
   */
  reactivateAffiliate: (affiliateId: string): Promise<ApiResponse<void>> =>
    apiClient.post(`/affiliates/${affiliateId}/reactivate`),

  /**
   * #1324 — mark an affiliate as having left the platform (admin only).
   * Server-side cascade: status=Left, discovery hidden, every affiliation
   * -> Ended, every commission rule -> inactive, audit row. 409 with
   * code AFFILIATE_ALREADY_LEFT when already Left.
   */
  leavePlatform: (affiliateId: string): Promise<ApiResponse<{ scheduledDisableAt: string }>> =>
    apiClient.post(`/affiliates/${affiliateId}/leave-platform`),

  /**
   * List all affiliates the current user can access, enriched for a
   * multi-select. Backed by GET /affiliates/mine (registered in T4).
   */
  listMine: (): Promise<ApiResponse<Array<{
    id: string;
    displayName: string;
    companyName?: string;
    role: string;
    // #1210 — Dedicated Affiliate classification.
    isDedicated?: boolean;
    // #1324 — raw affiliates.status plus the server's "has left" verdict.
    status?: string | null;
    hasLeftPlatform?: boolean;
  }>>> =>
    apiClient.get('/affiliates/mine'),
}

// #423 phase-1 slice-5: partnershipsApi retired along with PartnershipsPage.
// HAS-backed flows (/hotel-affiliations, /affiliate-hotels) go through
// hotelAffiliationsApi and affiliate-partnerships.api.

// ==================== Marketing Materials ====================

// Commission Rules / Calculations: the commissionRulesApi client was removed
// with the /commission-rules page — it had no other consumer. The hotel-scoped
// endpoints it wrapped still exist; the admin surface calls
// /commissions/rules (see services/api/commission-resolution.api.ts and
// components/commissions/CommissionRules.tsx).

// ==================== Payouts ====================

/**
 * Payout and bank account management endpoints
 */
export const payoutsApi = {

  /** #1998 B3 — PAYOUTS_ENABLED on this environment (any signed-in user). */
  getConfig: (): Promise<ApiResponse<{ enabled: boolean }>> =>
    apiClient.get('/payouts/config'),

  /**
   * #1596 — the affiliate's withdrawable balance per currency, with the
   * platform minimum, the open payout and what blocks a request.
   */
  getBalance: (): Promise<ApiResponse<PayoutBalanceSummary>> =>
    apiClient.get('/payouts/balance'),

  /**
   * #1596 — the affiliate's own payouts, newest first. `status` is compared
   * case-insensitively by the backend.
   */
  getPayouts: (params: {
    status?: PayoutStatus
    page?: number
    limit?: number
  } = {}): Promise<ApiResponse<Payout[]>> => {
    const query = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined))
    return apiClient.get('/payouts', { params: query })
  },

  /**
   * Request a payout of the FULL withdrawable balance in one currency (#1596).
   *
   * Backend Joi at `POST /api/v1/payouts/request` accepts ONLY:
   *   { bankAccountId (uuid), currency?, notes? }
   * and is pinned `.unknown(false)`: there is no `amount` any more (sending
   * one is a 400), and the historical `paymentMethod` is gone too. `currency`
   * is required only when the affiliate holds balances in several currencies.
   *
   * Refusals carry a `code`: 409 OPEN_PAYOUT_EXISTS, 422 BELOW_MINIMUM /
   * NO_WITHDRAWABLE_BALANCE / BANK_ACCOUNT_NOT_VERIFIED, 400 CURRENCY_REQUIRED.
   */
  requestPayout: (data: {
    bankAccountId: string
    currency?: string
    /** Optional free-text note: trimmed, max 500 characters; omit (or null) for none. */
    requestNote?: string | null
  }): Promise<ApiResponse<Payout>> =>
    apiClient.post('/payouts/request', data),

  /** #1596 — cancel the affiliate's own PENDING payout; its commissions are released. */
  cancelPayout: (payoutId: string): Promise<ApiResponse<void>> =>
    apiClient.patch(`/payouts/${encodeURIComponent(payoutId)}/cancel`),

  // ==================== Bank Accounts ====================

  /**
   * Get user's bank accounts
   */
  getBankAccounts: (): Promise<ApiResponse<any[]>> =>
    apiClient.get('/bank-accounts'),

  /**
   * Create a new bank account
   */
  createBankAccount: (data: {
    accountHolderName: string
    accountNumber: string
    // `routingNumber` is only applicable to US-style accounts; the backend
    // Joi schema treats it as optional (international accounts rely on
    // iban/swiftCode instead).
    routingNumber?: string
    bankName: string
    // accountType + country are REQUIRED by the backend Joi (POST /bank-accounts).
    accountType: BankAccountType
    country: string
    // #572: Israeli bank/branch numbers + international identifiers. iban +
    // swiftCode are accepted by the route Joi; bankCode carries the Israeli
    // bank number. NOTE: as of this writing BankAccountService.createBankAccount
    // does NOT persist iban/swiftCode/bankCode/metadata (it inserts only the
    // core columns) — see the #572 caveat in AffiliateDetailsPage.
    bankCode?: string
    swiftCode?: string
    iban?: string
    currency?: string
    nickname?: string
    metadata?: Record<string, unknown>
    isDefault?: boolean
  }): Promise<ApiResponse<any>> =>
    apiClient.post('/bank-accounts', data),

  /**
   * Update a bank account
   */
  updateBankAccount: (bankAccountId: string, data: Partial<any>): Promise<ApiResponse<any>> =>
    apiClient.put(`/bank-accounts/${bankAccountId}`, data),

  /**
   * #1595 — admin: every affiliate's bank accounts, newest first. Response
   * carries `pagination` { page, limit, total, totalPages }. Unset filters are
   * left out of the query string.
   */
  getAllBankAccountsAdmin: (filters: {
    status?: BankAccountStatus
    affiliateId?: string
    /** #2109: free text over affiliate name, company, email and code. */
    search?: string
    page?: number
    limit?: number
  } = {}): Promise<ApiResponse<AdminBankAccountRow[]>> => {
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== undefined && v !== ''),
    )
    return apiClient.get('/bank-accounts/admin/all', { params })
  },

  /**
   * #1595 — admin: set an account's status (approve = `verified`, reject =
   * `failed`). partner-service records the admin as verified_by and writes an
   * audit_log row with the reason (#1594).
   */
  /**
   * #2109 — admin: reveal one account's full bank details. partner-service
   * writes a BANK_DETAILS_VIEWED audit_log row for every call, so call it
   * only on an explicit admin click and never cache the result.
   */
  revealBankDetailsAdmin: (accountId: string): Promise<ApiResponse<RevealedBankDetails>> =>
    apiClient.post(`/bank-accounts/admin/${accountId}/reveal`),

  updateBankAccountStatus: (
    accountId: string,
    data: { status: BankAccountStatus; reason?: string },
  ): Promise<ApiResponse<BankAccount>> =>
    apiClient.patch(`/bank-accounts/admin/${accountId}/status`, data),

  // ==================== Payout Settings ====================

  /**
   * Canonical payout settings shape (see partner-service
   * `PayoutService.PayoutSettings` and Joi `updatePayoutSettingsSchema`).
   */

}

export default affiliatesApi
